/**
 * AmbassadorLayout - Peace of Sleep Ambassador Portal Layout
 *
 * Simplified version of POSLayout for brand ambassadors.
 * Black sidebar with gold accents, 2 nav items only.
 *
 * Brand Theme:
 * - Linen: #EAE7E1 (backgrounds)
 * - Gold: #BD982F (accents, buttons)
 * - Black: #000000 (text, headers)
 * - Light Grey: #C1BFBD (secondary text)
 */

import { PropsWithChildren, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../pos/pos-theme.css';
import './ambassador-layout.css';
import { useSidebarHidden } from '../../hooks/useSidebarHidden';
import { POS_GOLD } from '../../config/posRoomStatus';
import { PanelIcon } from '../../components/PanelIcon';


const ambassadorNavItems = [
  { path: '/ambassador', label: 'Dashboard', icon: '📊', end: true as const },
  { path: '/ambassador/training', label: 'Training Academy', icon: '🎓', end: false as const },
  { path: '/ambassador/properties', label: 'My Properties', icon: '🏢', end: false as const },
  { path: '/ambassador/proposals', label: 'My Proposals', icon: '📋', end: false as const },
  { path: '/ambassador/payout-setup', label: 'Payout Setup', icon: '🏦', end: false as const },
];

function AmbassadorLayout({ children }: PropsWithChildren) {
  const { user, signOut, getPortalAccess } = useAuth();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { hidden: sidebarHidden, toggle: toggleSidebar } = useSidebarHidden();
  const portalAccess = getPortalAccess();

  // Get current page title
  const currentPage = ambassadorNavItems.find(item =>
    item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
  );

  return (
    <div className={`portal-layout pos-themed${sidebarHidden ? ' sidebar-hidden' : ''}`}>
      {/* Sidebar */}
      <aside className={`sidebar pos-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header pos-sidebar-header">
          <div className="sidebar-brand pos-brand-name">PEACE OF SLEEP</div>
          <div className="sidebar-subtitle pos-brand-tagline">The Quiet Standard Of Trust</div>
        </div>

        {/* Ambassador Badge */}
        <div style={{
          padding: '12px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: 'var(--radius-lg)',
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            background: `rgba(189, 152, 47, 0.2)`,
            color: POS_GOLD
          }}>
            Brand Ambassador
          </span>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav pos-nav">
          {ambassadorNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) => `nav-link pos-nav-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer pos-sidebar-footer">
          <div className="user-menu-container pos-user-menu">
            <button
              className="user-menu-trigger pos-user-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div className="user-avatar pos-user-avatar">
                {user?.first_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="user-menu-info pos-user-info">
                <div className="user-name pos-user-name">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="user-email pos-user-email">{user?.email}</div>
              </div>
              <span className="user-menu-arrow">{userMenuOpen ? '▲' : '▼'}</span>
            </button>

            {userMenuOpen && (
              <div className="user-menu-dropdown pos-user-dropdown">
                {portalAccess.admin && (
                  <>
                    <Link
                      to="/"
                      className="user-menu-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span>⚡</span>
                      <span>Operations Portal</span>
                    </Link>
                    <div className="user-menu-divider" />
                  </>
                )}
                <button
                  className="user-menu-item user-menu-signout pos-signout"
                  onClick={() => {
                    signOut();
                    setUserMenuOpen(false);
                  }}
                >
                  <span>🚪</span>
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>

          {/* Branding */}
          <div className="pos-sentinel-footer">
            <div className="pos-sentinel-name">Sentinel Business Management</div>
            <div className="pos-sentinel-powered">Powered by CleanMyBed</div>
          </div>
        </div>
      </aside>

      {/* Mobile Header — hidden on desktop via CSS media query */}
      <header className="pos-mobile-header">
        <button
          className="pos-mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className="pos-mobile-header-center">
          <span className="pos-mobile-page-title">{currentPage?.label || 'Dashboard'}</span>
        </div>
        <div className="pos-mobile-spacer" />
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="pos-mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="main-content pos-main-content">
        <header className="top-bar pos-top-bar">
          <div className="pos-header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={toggleSidebar}
              title={sidebarHidden ? 'Show menu (Ctrl+B)' : 'Hide menu (Ctrl+B)'}
              aria-label={sidebarHidden ? 'Show menu' : 'Hide menu'}
              aria-pressed={sidebarHidden}
            >
              <PanelIcon />
            </button>
            <h1 className="page-title pos-page-title">{currentPage?.label || 'Dashboard'}</h1>
          </div>
          <div className="top-bar-actions">
            <div id="page-actions"></div>
          </div>
        </header>

        <div className="content-area pos-content-area">
          {children}
        </div>
      </main>
    </div>
  );
}

export default AmbassadorLayout;
